// Landasan pacu bandara: permukaan aspal gelap polos, garis tepi putih solid, marka tengah putih tebal
// putus-putus persis di sumbu, nomor landasan di ujung, threshold (bar putih) di dua ujung.
// Dukung landasan lurus sumbu-x / sumbu-y. Dipanggil engine.buildWorld setelah jalan.
import { TILE_W, TILE_H } from "./iso";

export function paintRunways(ctx, runwaySet, env) {
  if (!runwaySet || !runwaySet.size) return;
  const { gridToScreen } = env;
  const z = env.zoom || 1;
  const TW = TILE_W * z, TH = TILE_H * z;
  const has = (x, y) => runwaySet.has(x + "," + y);
  // Bangun komponen landasan (klaster tile yang saling terhubung)
  const seen = new Set();
  const clusters = [];
  for (const key of runwaySet) {
    if (seen.has(key)) continue;
    const [sx, sy] = key.split(",").map(Number);
    const stack = [[sx, sy]]; const comp = [];
    seen.add(key);
    while (stack.length) {
      const [x, y] = stack.pop();
      comp.push([x, y]);
      for (const [nx, ny] of [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]]) {
        const k = nx + "," + ny;
        if (runwaySet.has(k) && !seen.has(k)) { seen.add(k); stack.push([nx, ny]); }
      }
    }
    clusters.push(comp);
  }
  for (const comp of clusters) {
    const compSet = new Set(comp.map(p => p[0] + "," + p[1]));
    // sumbu dominan: bentang x vs y
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [x, y] of comp) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    const alongX = (maxX - minX) >= (maxY - minY);
    // 1. PERMUKAAN ASPAL polos (tanpa trotoar/marka jalan)
    for (const [x, y] of comp) {
      const N = gridToScreen(x, y), E = gridToScreen(x + 1, y);
      const S = gridToScreen(x + 1, y + 1), W = gridToScreen(x, y + 1);
      ctx.fillStyle = "#33393f"; // aspal landasan lebih gelap dari jalan
      ctx.beginPath();
      ctx.moveTo(N[0], N[1]); ctx.lineTo(E[0], E[1]); ctx.lineTo(S[0], S[1]); ctx.lineTo(W[0], W[1]);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#33393f"; ctx.lineWidth = 1; ctx.stroke();
    }
    // helper titik pada segmen layar landasan
    const lerp = (p, q, t) => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
    // 2. GARIS TEPI PUTIH solid di dua sisi luar (bukan di sambungan antar-tile / ujung)
    ctx.strokeStyle = "rgba(245,245,240,0.9)";
    ctx.lineCap = "butt";
    ctx.lineWidth = Math.max(0.6, TW * 0.012);
    for (const [x, y] of comp) {
      const N = gridToScreen(x, y), E = gridToScreen(x + 1, y);
      const S = gridToScreen(x + 1, y + 1), W = gridToScreen(x, y + 1);
      const [cx, cy] = gridToScreen(x + 0.5, y + 0.5);
      const inset = (p) => lerp(p, [cx, cy], 0.12); // sedikit menjorok ke dalam dari tepi
      // gambar garis tepi sebagai segmen memanjang penuh di sisi luar tile
      if (alongX) {
        if (!compSet.has(x + "," + (y - 1))) { const p1 = inset(N), p2 = inset(E); ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke(); }
        if (!compSet.has(x + "," + (y + 1))) { const p1 = inset(S), p2 = inset(W); ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke(); }
      } else {
        if (!compSet.has((x - 1) + "," + y)) { const p1 = inset(N), p2 = inset(W); ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke(); }
        if (!compSet.has((x + 1) + "," + y)) { const p1 = inset(E), p2 = inset(S); ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke(); }
      }
    }
    // 3. MARKA TENGAH putih tebal putus-putus di sumbu tengah pita (offset 0.5 tile
    //    agar pas di sambungan dua tile untuk pita lebar 2)
    const laneOff = 0.5; // tengah pita 2-tile
    ctx.strokeStyle = "#F5F5F0";
    ctx.lineWidth = Math.max(1.2, TW * 0.03);
    ctx.lineCap = "butt";
    if (alongX) {
      const yc = Math.round((minY + maxY) / 2) + laneOff; // garis tengah (fraksional)
      const a = gridToScreen(minX + 0.5, yc), b = gridToScreen(maxX + 0.5, yc); // margin dari ujung
      // dashes sepanjang landasan
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const dashL = TW * 0.5, gapL = TW * 0.4;
      let s = 0;
      while (s + dashL <= len) {
        const t0 = s / len, t1 = (s + dashL) / len;
        const p1 = lerp(a, b, t0), p2 = lerp(a, b, t1);
        ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke();
        s += dashL + gapL;
      }
    } else {
      const xc = Math.round((minX + maxX) / 2) + laneOff;
      const a = gridToScreen(xc, minY + 0.5), b = gridToScreen(xc, maxY + 0.5); // margin dari ujung
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const dashL = TW * 0.5, gapL = TW * 0.4;
      let s = 0;
      while (s + dashL <= len) {
        const t0 = s / len, t1 = (s + dashL) / len;
        const p1 = lerp(a, b, t0), p2 = lerp(a, b, t1);
        ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.stroke();
        s += dashL + gapL;
      }
    }
    // 4. THRESHOLD bar putih + NOMOR landasan di dua ujung (hanya saat cukup besar).
    // Semua DIGESER KE DALAM landasan agar tidak keluar dari tepi aspal.
    if (z >= 0.4) {
      // arah sumbu landasan di layar (untuk menggeser titik ke dalam sepanjang sumbu)
      const ux = alongX ? (TILE_W / 2) * z : -(TILE_W / 2) * z;
      const uy = (TILE_H / 2) * z;
      const ul = Math.hypot(ux, uy) || 1;
      const sux = ux / ul, suy = uy / ul; // vektor satuan sepanjang landasan
      const fs = Math.max(7, TH * 0.44); // nomor cukup besar tapi tetap di dalam landasan
      ctx.fillStyle = "rgba(245,245,240,0.92)";
      ctx.font = `800 ${fs}px 'Plus Jakarta Sans', sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      for (let e = 0; e < 2; e++) {
        // titik ujung landasan (tengah pita), lalu geser KE DALAM sepanjang sumbu
        let ex, ey;
        if (alongX) { ex = e === 0 ? minX : maxX + 1; ey = Math.round((minY + maxY) / 2) + 0.5; }
        else { ex = Math.round((minX + maxX) / 2) + 0.5; ey = e === 0 ? minY : maxY + 1; }
        let [bx, by] = gridToScreen(ex, ey);
        // arah "masuk" = berlawanan dari ujung: e=0 (ujung awal) → +sumbu, e=1 (ujung akhir) → -sumbu
        const inSign = e === 0 ? 1 : -1;
        // 1. THRESHOLD: 3 strip melintang, ditarik ke dalam ~0.9 tile dari bibir ujung
        const thrOff = (TW * 0.9) * inSign;
        const tx2 = bx + sux * thrOff, ty2 = by + suy * thrOff;
        ctx.save();
        ctx.translate(tx2, ty2);
        ctx.rotate(alongX ? Math.atan2(1, 2) : Math.atan2(1, -2));
        ctx.fillStyle = "rgba(245,245,240,0.92)";
        const bw = TW * 0.05, bl = TH * 0.5, sp = TH * 0.3; // bar melintang, lebih kecil
        for (const o of [-sp, 0, sp]) {
          ctx.fillRect(o - bl / 2, -bw / 2, bl, bw);
        }
        ctx.restore();
        // 2. NOMOR landasan: ditarik lebih ke dalam lagi (setelah threshold)
        if (z >= 0.6) {
          const numOff = (TW * 1.7) * inSign;
          const nx = bx + sux * numOff, ny = by + suy * numOff;
          ctx.save();
          ctx.translate(nx, ny);
          ctx.rotate(alongX ? Math.atan2(1, 2) : Math.atan2(1, -2));
          ctx.fillStyle = "rgba(245,245,240,0.92)";
          ctx.fillText(e === 0 ? "09" : "27", 0, 0);
          ctx.restore();
        }
      }
    }
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  }
}

// ---- API utama: dipanggil engine sekali per frame ----
// roadSet: Set "x,y". env: { gridToScreen, zoom, lamps }
